import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import { LinkTo } from '@ember/routing';
import Button from 'tio-ui/components/button';
import Input from 'tio-ui/components/input';
import Text from 'tio-ui/components/text';
import formatPhone from 'tio-common/helpers/format-phone';
import { reloadRecord, saveRecord } from 'tio-data/requests/save-record';
import AddressForm from 'tio-employee/components/pslf/annual-auto-submit/address-form';
import DetailsRow from 'tio-employee/components/pslf/annual-auto-submit/details-row';
import {
  formatPostalAddress,
  hasUnresolvedPrimaryEmail,
  mailingAddressFor,
  primaryEmailFor,
} from 'tio-employee/utils/pslf/contact-details';
import type { EmailAddressExtension as EmailAddressModel } from 'tio-data/data/resources/email-address.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { PslfAnnualAutoSubmitSource } from 'tio-data/data/resources/pslf-profile.type';
import type { IntlService } from 'ember-intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store } from '@warp-drive/core';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PROFILE_ROUTE = 'authenticated.profile';

type EditableRow = 'name' | 'email' | 'address';

export interface YourDetailsSignature {
  Args: {
    /** Read and written directly; a row shows the live record whenever there is one. */
    person: PersonModel;
    /** What the annual job would file; fills a row the live record leaves empty. */
    source?: PslfAnnualAutoSubmitSource | null;
  };
}

/** The "Your details" section of the PSLF settings dialog — one editable row per contact field. */
export default class AnnualYourDetails extends Component<YourDetailsSignature> {
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare sessionContext: SessionContextService;
  @service declare store: Store;

  @tracked editingRow?: EditableRow;
  @tracked firstNameDraft = '';
  @tracked lastNameDraft = '';
  @tracked emailDraft = '';
  @tracked hasSubmittedName = false;
  @tracked hasSubmittedEmail = false;
  // A create reaches `person.emailAddresses` only through a refetch, so the row that refetch
  // never delivers is held here rather than lost behind a "Not set".
  @tracked private savedEmail?: EmailAddressModel;

  get person() {
    return this.args.person;
  }

  get liveName() {
    return [this.person.firstName, this.person.lastName].filter(Boolean).join(' ').trim();
  }

  get filedName() {
    return this.liveName ? '' : (this.args.source?.borrowerName?.trim() ?? '');
  }

  get isNameFromForm() {
    return !!this.filedName;
  }

  get nameValue() {
    return this.liveName || this.filedName;
  }

  get currentEmail() {
    return primaryEmailFor(this.person) ?? this.savedEmail;
  }

  get isEmailUnresolved() {
    return !this.currentEmail && hasUnresolvedPrimaryEmail(this.person);
  }

  get filedEmail() {
    if (this.currentEmail || this.isEmailUnresolved) return '';
    return this.args.source?.borrowerEmail ?? '';
  }

  get isEmailFromForm() {
    return !!this.filedEmail;
  }

  get emailValue() {
    return this.currentEmail?.email ?? this.filedEmail;
  }

  get phoneNumber() {
    return this.person.primaryPhoneNumber?.number ?? '';
  }

  get mailingAddress() {
    return mailingAddressFor(this.person);
  }

  get filedAddress() {
    return this.mailingAddress ? undefined : this.args.source?.borrowerAddress;
  }

  get isAddressFromForm() {
    return !!formatPostalAddress(this.filedAddress);
  }

  get addressValue() {
    return formatPostalAddress(this.mailingAddress ?? this.filedAddress);
  }

  get isNameValid() {
    return !!this.firstNameDraft.trim() && !!this.lastNameDraft.trim();
  }

  get isEmailValid() {
    return EMAIL_PATTERN.test(this.emailDraft.trim());
  }

  #nameErrors(value: string, key: string) {
    if (!this.hasSubmittedName || value.trim()) return undefined;
    return [this.intl.t(`pslf.reimagined.annual_auto_submit.details.name.${key}`)];
  }

  get firstNameErrors() {
    return this.#nameErrors(this.firstNameDraft, 'first_required');
  }

  get lastNameErrors() {
    return this.#nameErrors(this.lastNameDraft, 'last_required');
  }

  get emailErrors() {
    if (!this.hasSubmittedEmail || this.isEmailValid) return undefined;
    return [this.intl.t('pslf.reimagined.annual_auto_submit.details.email.invalid')];
  }

  isEditing = (row: EditableRow) => this.editingRow === row;

  editName = () => {
    if (this.isNameFromForm) {
      const words = this.nameValue.split(/\s+/);
      this.lastNameDraft = words.length > 1 ? (words.pop() ?? '') : '';
      this.firstNameDraft = words.join(' ');
    } else {
      this.firstNameDraft = this.person.firstName ?? '';
      this.lastNameDraft = this.person.lastName ?? '';
    }
    this.hasSubmittedName = false;
    this.editingRow = 'name';
  };

  editEmail = () => {
    this.emailDraft = this.emailValue;
    this.hasSubmittedEmail = false;
    this.editingRow = 'email';
  };

  editAddress = () => {
    this.editingRow = 'address';
  };

  closeEditor = () => {
    this.editingRow = undefined;
  };

  setFirstName = (value: string) => {
    this.firstNameDraft = value;
  };

  setLastName = (value: string) => {
    this.lastNameDraft = value;
  };

  setEmail = (value: string) => {
    this.emailDraft = value;
  };

  reportFailure = (error: unknown) => {
    console.error(error);
    this.notifications.add(this.intl.t('pslf.reimagined.annual_auto_submit.details.save_error'), {
      appearance: 'error',
    });
  };

  saveName = dropTask(async () => {
    this.hasSubmittedName = true;
    if (!this.isNameValid) return;

    const person = this.person;
    person.firstName = this.firstNameDraft.trim();
    person.lastName = this.lastNameDraft.trim();

    try {
      await saveRecord(this.store, person);
      this.editingRow = undefined;
    } catch (error) {
      person.rollbackAttributes();
      this.reportFailure(error);
    }
  });

  saveEmail = dropTask(async () => {
    this.hasSubmittedEmail = true;
    if (!this.isEmailValid) return;

    const email = this.emailDraft.trim();
    const existing = this.currentEmail;

    if (existing) {
      existing.email = email;
      try {
        await saveRecord(this.store, existing);
        this.editingRow = undefined;
      } catch (error) {
        existing.rollbackAttributes();
        this.reportFailure(error);
      }
      return;
    }

    const created = this.store.createRecord<EmailAddressModel>('email-address', {
      email,
      contactType: 'PRIMARY',
      isActive: true,
      user: this.sessionContext.user,
    });

    try {
      await saveRecord(this.store, created);
    } catch (error) {
      created.unloadRecord();
      this.reportFailure(error);
      return;
    }

    this.savedEmail = created;
    this.editingRow = undefined;

    // `email-address` declares no inverse back to the person, so the refetch is what reconciles
    // the row with the server's own choice of correspondence address. The save already stands.
    try {
      await reloadRecord(this.store, this.person, { include: 'email-addresses' });
      this.savedEmail = undefined;
    } catch (error) {
      console.error(error);
    }
  });

  <template>
    <div class="tio-col" data-test-pslf-details-section>
      <Text @weight="semibold">
        {{t "pslf.reimagined.annual_auto_submit.details.heading"}}
      </Text>
      <Text @variant="muted" @size="sm">
        {{t "pslf.reimagined.annual_auto_submit.details.intro"}}
      </Text>

      <DetailsRow
        @rowKey="name"
        @label={{t "pslf.reimagined.annual_auto_submit.details.name.label"}}
        @value={{this.nameValue}}
        @isEditing={{this.isEditing "name"}}
        @onEdit={{this.editName}}
      >
        <:note>
          {{#if this.isNameFromForm}}
            <Text @variant="muted" @size="xs" data-test-pslf-details-from-form="name">
              {{t "pslf.reimagined.annual_auto_submit.details.from_last_form"}}
            </Text>
          {{/if}}
        </:note>
        <:form>
          <div class="tio-col-tight" data-test-pslf-details-form="name">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                @label={{t "pslf.reimagined.annual_auto_submit.details.name.first_label"}}
                @value={{this.firstNameDraft}}
                @onChange={{this.setFirstName}}
                @errors={{this.firstNameErrors}}
                @autocomplete="given-name"
                @testHook="pslf-details__first-name"
                data-test-pslf-details-input="firstName"
              />
              <Input
                @label={{t "pslf.reimagined.annual_auto_submit.details.name.last_label"}}
                @value={{this.lastNameDraft}}
                @onChange={{this.setLastName}}
                @errors={{this.lastNameErrors}}
                @autocomplete="family-name"
                @testHook="pslf-details__last-name"
                data-test-pslf-details-input="lastName"
              />
            </div>
            <div class="tio-row-tight flex-wrap">
              <Button
                @intent="primary"
                @size="sm"
                @isRunning={{this.saveName.isRunning}}
                @testHook="pslf-details__name-save"
                data-test-pslf-details-save="name"
                {{on "click" this.saveName.perform}}
              >
                {{t "pslf.reimagined.annual_auto_submit.details.save"}}
              </Button>
              <Button
                @appearance="link"
                @intent="default"
                @size="sm"
                @testHook="pslf-details__name-cancel"
                data-test-pslf-details-cancel="name"
                {{on "click" this.closeEditor}}
              >
                {{t "pslf.reimagined.annual_auto_submit.details.cancel"}}
              </Button>
            </div>
          </div>
        </:form>
      </DetailsRow>

      <DetailsRow
        @rowKey="email"
        @label={{t "pslf.reimagined.annual_auto_submit.details.email.label"}}
        @value={{this.emailValue}}
        @isEditing={{this.isEditing "email"}}
        @onEdit={{unless this.isEmailUnresolved this.editEmail}}
      >
        <:form>
          <div class="tio-col-tight" data-test-pslf-details-form="email">
            <Input
              @type="email"
              @label={{t "pslf.reimagined.annual_auto_submit.details.email.input_label"}}
              @hint={{t "pslf.reimagined.annual_auto_submit.details.email.hint"}}
              @value={{this.emailDraft}}
              @onChange={{this.setEmail}}
              @errors={{this.emailErrors}}
              @autocomplete="email"
              @testHook="pslf-details__email"
              data-test-pslf-details-input="email"
            />
            <div class="tio-row-tight flex-wrap">
              <Button
                @intent="primary"
                @size="sm"
                @isRunning={{this.saveEmail.isRunning}}
                @testHook="pslf-details__email-save"
                data-test-pslf-details-save="email"
                {{on "click" this.saveEmail.perform}}
              >
                {{t "pslf.reimagined.annual_auto_submit.details.save"}}
              </Button>
              <Button
                @appearance="link"
                @intent="default"
                @size="sm"
                @testHook="pslf-details__email-cancel"
                data-test-pslf-details-cancel="email"
                {{on "click" this.closeEditor}}
              >
                {{t "pslf.reimagined.annual_auto_submit.details.cancel"}}
              </Button>
            </div>
          </div>
        </:form>
        <:note>
          <Text @variant="muted" @size="xs" data-test-pslf-details-note="email">
            {{#if this.isEmailUnresolved}}
              {{t "pslf.reimagined.annual_auto_submit.details.email.unavailable"}}
            {{else}}
              {{t "pslf.reimagined.annual_auto_submit.details.email.hint"}}
            {{/if}}
          </Text>
          {{#if this.isEmailFromForm}}
            <Text @variant="muted" @size="xs" data-test-pslf-details-from-form="email">
              {{t "pslf.reimagined.annual_auto_submit.details.from_last_form"}}
            </Text>
          {{/if}}
        </:note>
      </DetailsRow>

      <DetailsRow
        @rowKey="phone"
        @label={{t "pslf.reimagined.annual_auto_submit.details.phone.label"}}
        @value={{formatPhone this.phoneNumber}}
        @isEditing={{false}}
      >
        <:note>
          <Text @variant="muted" @size="xs" data-test-pslf-details-note="phone">
            {{t "pslf.reimagined.annual_auto_submit.details.phone.managed_note"}}
            <LinkTo
              @route={{PROFILE_ROUTE}}
              class="app-text-link app-text-link--inline tio-text-xs-semibold"
              data-test-pslf-details-phone-link
            >
              {{t "pslf.reimagined.annual_auto_submit.details.phone.managed_link"}}
            </LinkTo>
          </Text>
        </:note>
      </DetailsRow>

      <DetailsRow
        @rowKey="address"
        @label={{t "pslf.reimagined.annual_auto_submit.details.address.label"}}
        @value={{this.addressValue}}
        @isEditing={{this.isEditing "address"}}
        @onEdit={{this.editAddress}}
      >
        <:form>
          <AddressForm
            @address={{this.mailingAddress}}
            @prefill={{this.filedAddress}}
            @person={{this.person}}
            @onSaved={{this.closeEditor}}
            @onCancel={{this.closeEditor}}
            @onError={{this.reportFailure}}
          />
        </:form>
        <:note>
          {{#if this.isAddressFromForm}}
            <Text @variant="muted" @size="xs" data-test-pslf-details-from-form="address">
              {{t "pslf.reimagined.annual_auto_submit.details.from_last_form"}}
            </Text>
          {{/if}}
        </:note>
      </DetailsRow>
    </div>
  </template>
}
